/// <amd-module name="Analytics/Shared/TrendChart/Common/DurationNormalizer" />
import { DurationConverter } from "Analytics/Shared/TrendChart/Common/DurationConverter";
import * as Resources from "Analytics/Shared/TrendChart/Common/Resources";
import { INormalizedDuration } from "Analytics/Shared/TrendChart/Common/Types";

const HoursThresholdValue = 2;
const MinutesThresholdValue = 10;
const ThresholdPercentageValue = 50;

export class DurationNormalizer {
    /**
     * normalizeDurationArray
     * Normalizes duration for a nested array between seconds, minutes and hours
     * @param durationValues A nested array which is normalized after tallying individual averages of member arrays, ignoring zero values
     * @param considerFlattened To flatten durationValues, if needed
     * @returns INormalizedDuration Object which has durationUnit and normalized Time
     */
    public normalizeDurations(durationValues: number[][], considerFlattened: boolean = false): INormalizedDuration {
        let processedDurationArray: number[];
        // If considerFlattened is true, then we consider values to be in a single array without
        // being stacked in categories, and thus don't take averages
        if (considerFlattened) {
            let flattendDurationArray: number[];
            flattendDurationArray = ([] as number[]).concat(...durationValues);
            processedDurationArray = flattendDurationArray;
        } else {
            const averagedDurationValues = durationValues.map(durationArray => {
                return this._calculateAverageIgnoringZeros(durationArray);
            });
            processedDurationArray = averagedDurationValues;
        }

        // Assign seconds bucket by default
        let normalizeConverterFunction: (number: number) => number;
        let normalizedDurationUnitString: string = Resources.UnitInSeconds;
        let normalizeConverterFunctionAssigned = false;
        if (this._shouldNormalizeDurationsToHours(processedDurationArray)) {
            // Hours bucket - If 50% values are above hours threshold, then assign hours bucket
            normalizeConverterFunction = DurationConverter.convertSecondsToHours;
            normalizedDurationUnitString = Resources.UnitInHours;
            normalizeConverterFunctionAssigned = true;
        } else if (this._shouldNormalizeDurationsToMinutes(processedDurationArray)) {
            // Minutes Bucket - If 50% values are above threshold, then assign minutes bucket
            normalizeConverterFunction = DurationConverter.convertSecondsToMinutes;
            normalizedDurationUnitString = Resources.UnitInMinutes;
            normalizeConverterFunctionAssigned = true;
        }

        if (normalizeConverterFunctionAssigned) {
            durationValues = durationValues.map(durationInSecondsList => {
                return durationInSecondsList.map(durationInSeconds => {
                    return normalizeConverterFunction(durationInSeconds);
                });
            });
        }

        return {
            durationUnitString: normalizedDurationUnitString,
            durationValues: durationValues
        } as INormalizedDuration;
    }

    /**
     * Returns the normalized duration for a singular value according to set thresholds
     * @param durationInSeconds Value of duration (in seconds)
     * @returns INormalizedDuration Object which has durationUnit and normalized Time
     */
    public normalizeDuration(durationInSeconds: number): INormalizedDuration {
        let normalizedDuration: number;

        normalizedDuration = DurationConverter.convertSecondsToHours(durationInSeconds);
        if (normalizedDuration > HoursThresholdValue) {
            return {
                durationUnitString: Resources.UnitInHours,
                durationValues: [[normalizedDuration]]
            } as INormalizedDuration;
        }
        normalizedDuration = DurationConverter.convertSecondsToMinutes(durationInSeconds);
        if (normalizedDuration > MinutesThresholdValue) {
            return {
                durationUnitString: Resources.UnitInMinutes,
                durationValues: [[normalizedDuration]]
            } as INormalizedDuration;
        }

        return {
            durationUnitString: Resources.UnitInSeconds,
            durationValues: [[durationInSeconds]]
        } as INormalizedDuration;
    }

    private _shouldNormalizeDurationsToHours(durationInSecondsArray: number[]): boolean {
        return this._normalizeDurationOnFrequency(
            durationInSecondsArray,
            HoursThresholdValue,
            ThresholdPercentageValue,
            DurationConverter.convertSecondsToHours
        );
    }

    private _shouldNormalizeDurationsToMinutes(durationInSecondsArray: number[]): boolean {
        return this._normalizeDurationOnFrequency(
            durationInSecondsArray,
            MinutesThresholdValue,
            ThresholdPercentageValue,
            DurationConverter.convertSecondsToMinutes
        );
    }

    private _normalizeDurationOnFrequency(
        durationInSecondsArray: number[],
        thresholdValue: number,
        percentageThresholdRequired: number,
        converterFunction: (number: number) => number
    ): boolean {
        const nonZeroDurationsArray = durationInSecondsArray.filter(duration => duration > 0);
        const expectedEntriesExceedingThreshold = Math.floor(nonZeroDurationsArray.length * (percentageThresholdRequired / 100));
        let numValuesGreaterThanThreshold = 0;
        nonZeroDurationsArray.forEach(
            (duration: number): number => {
                const convertedValue = converterFunction(duration);
                if (convertedValue > thresholdValue) {
                    numValuesGreaterThanThreshold++;
                }
                return convertedValue;
            }
        );

        return numValuesGreaterThanThreshold > expectedEntriesExceedingThreshold;
    }

    private _calculateAverageIgnoringZeros(durationArray: number[]): number {
        if (!durationArray || durationArray.length === 0) {
            return 0;
        }
        let sumForDurationArray = 0;
        let nonZeroCount = 0;
        durationArray.forEach(duration => {
            if (duration) {
                nonZeroCount++;
            }
            sumForDurationArray += duration;
        });

        return nonZeroCount !== 0 ? sumForDurationArray / nonZeroCount : 0;
    }
}
